// Quyết toán idle gộp: bế quan (cultivation) + Lịch Luyện (trial). Cùng trần 72h, cùng khối 8h.
// Bế quan thuần giữ nguyên đường tính của M1. Lịch Luyện chia khối 8h, và trong khối cắt thêm ở mốc
// đột phá tiểu cảnh dự tính: mỗi đoạn dựng lại ngữ cảnh (L quái, Giai đồ, ATK) từ trạng thái đầu
// đoạn, tính tu vi 30% + roll các lần hạ, tu vi phụ cộng ở cuối đoạn. Nhờ vậy một khối offline và
// online từng phút cho cùng phân phối kể cả khi đang lên bậc.
import { BALANCE } from '@data/balance';
import { zoneDef } from '@data/content/zones';
import { HOUR_MS } from '../core/time';
import {
  cultCost,
  efficiency,
  grantQi,
  qiRate,
  settleIdle,
  stepInfo,
  MAX_STEP,
  type CultivationState,
  type IdleSettleReport,
} from '../cultivation';
import {
  addLoot,
  emptyLoot,
  killsPerMin,
  settleTrial,
  sideQiPerKill,
  type TrialCtx,
  type TrialLoot,
  type TrialState,
} from '../trial';

export interface WorldState {
  cult: CultivationState;
  trial: TrialState;
}

export interface WorldSettle {
  world: WorldState;
  report: IdleSettleReport;
  loot: TrialLoot;
}

/**
 * Đoạn ngắn nhất khi cắt ở mốc đột phá (độ phân giải như quyết toán online từng phút). Tu vi phụ
 * rơi theo từng lần hạ nên mốc dự tính có thể hụt một chút; đoạn sàn giữ số đoạn hữu hạn.
 */
const SPLIT_MIN_MS = 60_000;

function mergeReport(a: IdleSettleReport, b: IdleSettleReport): IdleSettleReport {
  return {
    ...a,
    toMs: b.toMs,
    qiGained: a.qiGained + b.qiGained,
    overflowGained: a.overflowGained + b.overflowGained,
    wastedQi: a.wastedQi + b.wastedQi,
    masteryGained: a.masteryGained + b.masteryGained,
    minorBreakthroughs: [...a.minorBreakthroughs, ...b.minorBreakthroughs],
    worldNews: [...a.worldNews, ...b.worldNews],
    rareLines: [...a.rareLines, ...b.rareLines],
    deathSeclusionEndedAtMs: b.deathSeclusionEndedAtMs ?? a.deathSeclusionEndedAtMs,
  };
}

/**
 * Mốc kết thúc đoạn bắt đầu ở `s`: mốc dự tính của lần đột phá tiểu cảnh kế tiếp (tu vi bế quan 30% +
 * tu vi phụ kỳ vọng), không sớm hơn `s + SPLIT_MIN_MS`, không quá `end`.
 */
function segmentEnd(
  cult: CultivationState,
  zoneId: string,
  ctx: TrialCtx,
  s: number,
  end: number,
  eff: number,
): number {
  if (stepInfo(cult.step).isLast || cult.step >= MAX_STEP) return end;
  const need = cultCost(cult.step) - cult.cultivationXp;
  const z = zoneDef(zoneId);
  const perMs =
    (qiRate(cult, s) * eff * BALANCE.TRIAL_QI_SHARE) / 1000 +
    (sideQiPerKill(z, ctx.playerL) * killsPerMin(z, ctx) * ctx.eff) / 60_000;
  if (!(perMs > 0)) return end;
  return Math.min(end, s + Math.max(SPLIT_MIN_MS, Math.ceil(need / perMs)));
}

/**
 * `mkCtx(cult, eff)` dựng ngữ cảnh Lịch Luyện (ATK, dòng tiện ích…) từ trạng thái đầu đoạn;
 * lớp state / sim cấp vì chỉ số người chơi phụ thuộc trang bị.
 */
export function settleWorld(
  input: WorldState,
  fromMs: number,
  toMs: number,
  seed: number,
  online: boolean,
  mkCtx: (cult: CultivationState, eff: number) => TrialCtx,
): WorldSettle {
  const loot = emptyLoot();
  const cult0 = input.cult;
  if (cult0.idle.mode !== 'trial' || !cult0.idle.trialZoneId) {
    const r = settleIdle(cult0, fromMs, toMs, seed, online);
    return { world: { ...input, cult: r.state }, report: r.report, loot };
  }
  const zoneId = cult0.idle.trialZoneId;
  const elapsed = Math.max(0, toMs - fromMs);
  const effective = Math.min(elapsed, BALANCE.OFFLINE_CAP_HOURS * HOUR_MS);
  const stopMs = fromMs + effective;
  const blockMs = BALANCE.OFFLINE_BLOCK_HOURS * HOUR_MS;
  let cult = cult0;
  let trial = input.trial;
  let report: IdleSettleReport | null = null;
  for (let t = fromMs; t < stopMs;) {
    const blockEnd = Math.min(t + blockMs, stopMs);
    for (let s = t; s < blockEnd;) {
      const eff = efficiency(cult, online);
      const ctx = mkCtx(cult, eff);
      const e = segmentEnd(cult, zoneId, ctx, s, blockEnd, eff);
      const tr = settleTrial(trial, zoneId, s, e, seed, ctx);
      trial = tr.state;
      addLoot(loot, tr.loot);
      const r = settleIdle(cult, s, e, seed, online);
      cult = r.state;
      report = report ? mergeReport(report, r.report) : r.report;
      if (tr.loot.qi > 0) {
        const g = grantQi(cult, tr.loot.qi, e);
        cult = g.state;
        report.qiGained += tr.loot.qi;
        report.overflowGained += g.overflowGained;
        report.wastedQi += g.wasted;
        report.minorBreakthroughs.push(...g.breakthroughs);
        report.worldNews.push(...g.news);
      }
      s = e;
    }
    t = blockEnd;
  }
  if (!report) {
    const r = settleIdle(cult, fromMs, fromMs, seed, online);
    report = r.report;
  }
  report = { ...report, fromMs, toMs, online, cappedMs: elapsed - effective };
  cult = { ...cult, idle: { ...cult.idle, lastSettledMs: Math.max(cult.idle.lastSettledMs, toMs) } };
  return { world: { cult, trial }, report, loot };
}
